import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import countryList from 'react-select-country-list';
import { apiCall } from '../../../utils/httpClient';
import ApiEndPoint from '../../../utils/apiEndPoints';
import { SIGN_UP, ORGANIZATION_TITLES } from '../constant';
import styles from '../index.module.css';

import emailIcon from '../../../images/mail.svg';
import phoneIcon from '../../../images/phone.png';
import avatarIcon from '../../../images/human.svg';
import doctorIcon from '../../../images/doctorIcon.png';
import arrowLeft from '../../../images/Icons/arrow_left.svg';
import arrowDown from '../../../images/stat_minus_1.png';
import organizationIcon from '../../../images/orgnisation.png';
import organizationTitleIcon from '../../../images/organization_title.png';
import reasonIcon from '../../../images/question-mark.png';
import locationIcon from '../../../images/location.png';

const SignupForm = () => {
  const navigate = useNavigate();
  
  // Step State
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 2;
  const [submitInProgress, setSubmitInProgress] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    doctorId: '',
    organizationName: '',
    orgTitle: '',
    country: '',
    reason: '',
    isRegistered: false,
    termsAccepted: false
  });

  const [touched, setTouched] = useState({});
  const [doctorRespCode, setDoctorRespCode] = useState(false);
  const emailRef = useRef(null);
  const contactRef = useRef(null);

  // Initialize Countries
  const countryOptions = countryList().getData();

  // Doctor Code Validation Effect
  useEffect(() => {
    const validateDoctor = async () => {
      const doctorCode = formData.doctorId?.trim();
      if (formData.isRegistered && doctorCode) {
        try {
          const endpoint = `${ApiEndPoint.SIGNUP_DOCTOR}/${doctorCode}`;
          const response = await apiCall('GET', endpoint);
          const isValid = response?.data?.payLoad === true;
          setDoctorRespCode(isValid);
        } catch (error) {
          console.error('Doctor validation failed:', error);
          setDoctorRespCode(false);
        }
      } else {
        setDoctorRespCode(false);
      }
    };
    validateDoctor();
  }, [formData.isRegistered, formData.doctorId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const markAsTouched = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  // Validations
  const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isContactValid = (num) => !num || /^[0-9]+$/.test(num);

  const validateStep1 = () => {
    const fields = ['firstName', 'lastName', 'email'];
    if (formData.isRegistered) {
      fields.push('doctorId');
    }
    let valid = true;
    fields.forEach(f => {
      markAsTouched(f);
      if (!formData[f]) valid = false;
    });

    if (formData.isRegistered && !doctorRespCode) {
      markAsTouched('doctorId');
      valid = false;
    }
    if (!isEmailValid(formData.email)) valid = false;
    if (!isContactValid(formData.contactNumber)) valid = false;
    return valid;
  };

  const nextStep = () => {
    if (validateStep1()) setCurrentStep(2);
  };

  const prevStep = () => setCurrentStep(1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fields = ['organizationName', 'orgTitle', 'country'];
    fields.forEach(f => markAsTouched(f));

    if (fields.every(f => formData[f]) && formData.termsAccepted) {
      setSubmitInProgress(true);
      try {
        const payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          contactNumber: formData.contactNumber,
          organisationName: formData.organizationName,
          organisationTitle: formData.orgTitle,
          country: formData.country,
          reasonForSignUp: formData.reason,
          doctorAnalyst: formData.isRegistered,
          doctorCode: formData.doctorId,
        };

        const response = await apiCall('POST', ApiEndPoint.SIGNUP, payload);

        if (response.data.success) {
          setCurrentStep(3);
        } else if (response.data.message === "Email Already Exists") {
          setTouched(prev => ({ ...prev, email: true }));
          setFormData(prev => ({ ...prev, emailError: 'Email Already Exists' }));
          setCurrentStep(1);
          setTimeout(() => {
            emailRef.current?.focus();
            emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }, 100);
        } else if (response.data.message === "Mobile Number Already Exists") {
          setTouched(prev => ({ ...prev, contactNumber: true }));
          setFormData(prev => ({ ...prev, contactError: 'Mobile Number Already Exists' }));
          setCurrentStep(1);
          setTimeout(() => {
            contactRef.current?.focus();
            contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }, 100);
        } else {
          alert(response.data.message || 'Signup failed');
        }
      } catch (error) {
        console.error("Signup error:", error);
        alert('Server is not reachable. Please try again later.');
      } finally {
        setSubmitInProgress(false);
      }
    }
  };

  const progressPercent = (currentStep / totalSteps) * 100;

  return (
    <div className={`col-12 col-md-4 ${styles['right-section']}`}>
      <div className={styles.login_panel}>
        <form className={styles['signup-form']} onSubmit={handleSubmit}>

          {currentStep === 3 && (
            <div className={`${styles.company_logo} text-center`}>
              <img src='assets/GCD-3.svg' alt="Logo" />
            </div>
          )}

          <h6>{SIGN_UP.TITLES.HEADER}</h6>

          {(currentStep === 1 || currentStep === 2) && (
            <div className={styles['progress-wrapper']}>
              <div className={styles['progress-header']}>
                <div className={styles['progress-track']}>
                  <div className={styles['progress-fill']} style={{ width: `${progressPercent}%` }}></div>
                </div>
              </div>
              <div className={styles['progress-header-row']}>
                {currentStep === 2 && (
                  <a className={styles['back-link']} onClick={prevStep} role="button">
                    <img src={arrowLeft} alt=""/>&nbsp;&nbsp;Back
                  </a>
                )}
                <div className={styles['step-text']}>{SIGN_UP.TITLES.STEP_TEXT(currentStep, totalSteps)}</div>
              </div>
            </div>
          )}

          {/* Step 1 */}
          {currentStep === 1 && (
            <>
              <span className={styles['step-1-header']}>{SIGN_UP.TITLES.USER_DETAILS}</span>
              <div className={styles['form-group']}>
                <span className={styles.icon}><img src={avatarIcon} alt="" /></span>
                <input
                  type="text" name="firstName" className={`form-control ${touched.firstName && !formData.firstName ? styles.error : ''}`}
                  placeholder={SIGN_UP.PLACEHOLDERS.FIRST_NAME} value={formData.firstName}
                  onChange={handleInputChange} onBlur={() => markAsTouched('firstName')}
                />
                {touched.firstName && !formData.firstName && <div className={styles['error-message']}>{SIGN_UP.ERRORS.FIRST_NAME_REQUIRED}</div>}
              </div>

              <div className={styles['form-group']}>
                <span className={styles.icon}><img src={avatarIcon} alt="" /></span>
                <input
                  type="text" name="lastName" className={`form-control ${touched.lastName && !formData.lastName ? styles.error : ''}`}
                  placeholder={SIGN_UP.PLACEHOLDERS.LAST_NAME} value={formData.lastName}
                  onChange={handleInputChange} onBlur={() => markAsTouched('lastName')}
                />
                {touched.lastName && !formData.lastName && <div className={styles['error-message']}>{SIGN_UP.ERRORS.LAST_NAME_REQUIRED}</div>}
              </div>

              <div className={styles['form-group']}>
                <span className={styles.icon}><img src={emailIcon} alt="" /></span>
                <input
                  type="email" name="email" className={`form-control ${(touched.email && (!formData.email || !isEmailValid(formData.email))) || formData.emailError ? styles.error : ''}`}
                  placeholder={SIGN_UP.PLACEHOLDERS.EMAIL} value={formData.email}
                  onChange={handleInputChange} onBlur={() => markAsTouched('email')}
                  ref={emailRef}
                />
                {touched.email && !formData.email && <div className={styles['error-message']}>{SIGN_UP.ERRORS.EMAIL_REQUIRED}</div>}
                {touched.email && formData.email && !isEmailValid(formData.email) && <div className={styles['error-message']}>{SIGN_UP.ERRORS.EMAIL_INVALID}</div>}
                {formData.emailError && <div className={styles['error-message']}>{formData.emailError}</div>}
              </div>

              <div className={styles['form-group']}>
                <span className={styles.icon}><img src={phoneIcon} alt="" /></span>
                <input
                  type="text" name="contactNumber" className={`form-control ${(touched.contactNumber && !isContactValid(formData.contactNumber)) || formData.contactError ? styles.error : ''}`}
                  placeholder={SIGN_UP.PLACEHOLDERS.CONTACT} value={formData.contactNumber}
                  onChange={handleInputChange} onBlur={() => markAsTouched('contactNumber')}
                  ref={contactRef}
                />
                {touched.contactNumber && !isContactValid(formData.contactNumber) && <div className={styles['error-message']}>{SIGN_UP.ERRORS.CONTACT_INVALID}</div>}
                {formData.contactError && <div className={styles['error-message']}>{formData.contactError}</div>}
              </div>

              <div className={styles['terms-checkbox']}>
                <input
                  type="checkbox"
                  id="isRegistered"
                  name="isRegistered"
                  checked={formData.isRegistered}
                  onChange={handleInputChange}
                  style={{
                    width: '16px',
                    height: '16px',
                    minWidth: '16px',
                    marginTop: '2px',
                    cursor: 'pointer',
                    borderRadius: '3px',
                    position: 'relative',
                    display: 'inline-block',
                  }}
                />
                <label htmlFor="isRegistered" className={styles['checkbox-label']}>
                  Are you registered with 4basecare ?
                </label>
              </div>

              {formData.isRegistered && (
                <div className={styles['form-group']}>
                  <span className={styles.icon}><img src={doctorIcon} alt="" /></span>
                  <input
                    type="text"
                    name="doctorId"
                    className={`form-control ${touched.doctorId && !formData.doctorId ? styles.error : ''}`}
                    placeholder={SIGN_UP.PLACEHOLDERS.DOCTOR_ID}
                    value={formData.doctorId}
                    onChange={handleInputChange}
                    onBlur={() => markAsTouched('doctorId')}
                  />
                  {(touched.doctorId && !formData.doctorId) && <div className={styles['error-message']}>{SIGN_UP.ERRORS.DOCTOR_ID_REQUIRED}</div>}
                  {(touched.doctorId && formData.doctorId && !doctorRespCode) && <div className={styles['error-message']}>Please enter a valid Doctor code</div>}
                </div>
              )}

              <button type="button" className={styles['next-button']} onClick={nextStep}>{SIGN_UP.BUTTONS.NEXT}</button>
            </>
          )}

          {/* Step 2 */}
          {currentStep === 2 && (
            <>
              <span className={styles['step-1-header']}>{SIGN_UP.TITLES.ORG_DETAILS}</span>
              <div className={styles['form-group']}>
                <span className={styles.icon}><img src={organizationIcon} alt="" /></span>
                <input
                  type="text" name="organizationName" className="form-control"
                  placeholder={SIGN_UP.PLACEHOLDERS.ORG_NAME} value={formData.organizationName}
                  onChange={handleInputChange} onBlur={() => markAsTouched('organizationName')}
                />
                {touched.organizationName && !formData.organizationName && <div className={styles['error-message']}>{SIGN_UP.ERRORS.ORG_NAME_REQUIRED}</div>}
              </div>

              <div className={`${styles['form-group']} ${styles['select-wrapper']}`}>
                <span className={styles.icon}><img src={organizationTitleIcon} alt="" /></span>
                <select name="orgTitle" className="form-control" value={formData.orgTitle} onChange={handleInputChange}>
                  {ORGANIZATION_TITLES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <span className={styles['chevron-icon']}><img src={arrowDown} alt="" /></span>
              </div>

              <div className={`${styles['form-group']} ${styles['select-wrapper']}`}>
                <span className={styles.icon}><img src={locationIcon} alt="" /></span>
                <select name="country" className="form-control" value={formData.country} onChange={handleInputChange}>
                  <option value="">Country*</option>
                  {countryOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <span className={styles['chevron-icon']}><img src={arrowDown} alt="" /></span>
              </div>

              <div className={styles['form-group']}>
                <span className={styles.icon}><img src={reasonIcon} alt="" /></span>
                <input type="text" name="reason" className="form-control" placeholder={SIGN_UP.PLACEHOLDERS.REASON} value={formData.reason} onChange={handleInputChange} />
              </div>

              <div className={styles['terms-checkbox']}>
                <input type="checkbox" id="terms" style={{
                  width: '16px',
                  height: '16px',
                  minWidth: '16px',
                  marginTop: '2px',
                  cursor: 'pointer',
                  borderRadius: '3px',
                  position: 'relative',
                  display: 'inline-block',
                }} name="termsAccepted" checked={formData.termsAccepted} onChange={handleInputChange} />
                <label htmlFor="terms" className={styles['checkbox-label']}>
                  {SIGN_UP.TERMS.AGREE_TEXT} <a href="#" className={styles['terms-link']}>{SIGN_UP.TERMS.TERMS_OF_SERVICE}</a> and <a href="#" className={styles['terms-link']}>{SIGN_UP.TERMS.PRIVACY_POLICY}</a>
                </label>
              </div>

              <button type="submit" className={styles['next-button']} disabled={!formData.termsAccepted || submitInProgress}>
                {SIGN_UP.BUTTONS.SIGN_UP}
              </button>
            </>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && (
            <div className="text-center">
              <p className={styles['normal-text']}>{SIGN_UP.TITLES.ALMOST_THERE}</p><br />
              <p className={styles['normal-text']}>
                {SIGN_UP.TITLES.ACCOUNT_CREATED} We've sent your credentials to <br />
                <span className={styles['email-text']}>[{formData.email}]</span>.
              </p>
              <button type="button" className={styles['next-button']} style={{ marginTop: '20px' }} onClick={() => navigate('/login')}>
                {SIGN_UP.BUTTONS.LOG_IN}
              </button>
            </div>
          )}

          {/* Footer */}
          {(currentStep === 1 || currentStep === 2) && (
            <div className={styles.signup_footer}>
              <div className={styles.sign_in}>
                <span>{SIGN_UP.FOOTER.ALREADY_ACCOUNT} <a className={styles['sign_in-text']} onClick={() => navigate('/login')}>{SIGN_UP.FOOTER.SIGN_IN_TEXT}</a></span>
              </div>
            </div>
          )}

        </form>
      </div>
    </div>
  );
};

export default SignupForm;
